import React, { useState, useCallback, useEffect } from 'react';
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import './App.css';
import './styles/tokens.css';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import LoginSignupPage from './components/LoginSignupPage';
import ForgotPasswordPage from './components/ForgotPasswordPage';
import ResetPasswordPage from './components/ResetPasswordPage';
import RecommendationSelector from './components/RecommendationSelector';
import ProfessionalHeader from './components/ProfessionalHeader';
import AdvancedRecommendationForm from './components/AdvancedRecommendationForm';
import ProtectedRoute from './components/ProtectedRoute';
import ExpertDashboard from './pages/ExpertDashboard';
import ExpertLabelingPage from './pages/ExpertLabelingPage';
import SecretCorner from './pages/SecretCorner';
import FittersCornerPage from './pages/FittersCornerPage';
import BayModePage from './pages/BayModePage';
import ExplorePage from './pages/ExplorePage';
import AdminUsersPage from './pages/AdminUsersPage';
import MyBagPage from './pages/MyBagPage';
import UserDashboard from './pages/UserDashboard';
import ProfileReviewPage from './pages/ProfileReviewPage';
import PostFittingReviewPage from './pages/PostFittingReviewPage';
import QuickFitPage from './pages/QuickFitPage';
import QuickRecommendationPage from './pages/QuickRecommendationPage';
import FittingHistoryPage from './pages/FittingHistoryPage';
import MLRecommendationResults from './components/MLRecommendationResults';
import WedgeRecommendationResults from './components/WedgeRecommendationResults';
import { createAdvancedProfile, getMLDriverRecommendations3Stage, getWedgeRecommendationsRuleBased, getDriverRecommendationsV9, getIronRecommendationsV9, MLRecommendationsResponse, getMyProfileCount } from './services/recommendationService';
import ScoringModeToggle, { ScoringMode } from './components/ScoringModeToggle';

// Helper function to translate Pydantic validation errors into user-friendly messages
function parseValidationError(error: any): string {
  // Check if this is a Pydantic validation error
  if (error.response?.data?.detail && Array.isArray(error.response.data.detail)) {
    const errors = error.response.data.detail;

    // Field name mapping for user-friendly display
    const fieldNames: Record<string, string> = {
      'shot_count_driver': 'Driver Shot Count',
      'shot_count_7i': '7-Iron Shot Count',
      'shot_count_wedge1': 'Wedge Shot Count',
      'ball_speed_driver': 'Driver Ball Speed',
      'swing_speed_driver': 'Driver Swing Speed',
      'handicap': 'Handicap',
      'age': 'Age',
      'height_inches': 'Height',
      // Add more as needed
    };

    const errorMessages = errors.map((err: any) => {
      const field = err.loc[err.loc.length - 1]; // Last element is the field name
      const fieldLabel = fieldNames[field] || field.replace(/_/g, ' ');
      const input = err.input;

      // Translate different error types
      if (err.type === 'less_than_equal') {
        return `${fieldLabel}: Must be ${err.ctx.le} or less (you entered ${input})`;
      } else if (err.type === 'greater_than_equal') {
        return `${fieldLabel}: Must be ${err.ctx.ge} or greater (you entered ${input})`;
      } else if (err.type === 'missing') {
        return `${fieldLabel}: This field is required`;
      } else {
        return `${fieldLabel}: ${err.msg}`;
      }
    });

    return errorMessages.join('\n');
  }

  // Fallback to original error message
  return error.message || 'An error occurred. Please check your inputs and try again.';
}

// Home/Selector Page (Public)
const HomePage: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleSelectQuick = useCallback(() => {
    // Go directly to quick fitting - no demographics check
    navigate('/quick');
  }, [navigate]);

  const handleSelectAdvanced = useCallback(() => {
    // Go directly to advanced fitting - no demographics check
    navigate('/advanced');
  }, [navigate]);

  return (
    <>
      <ProfessionalHeader user={user} onLogout={logout} />
      {(user?.is_secret_corner || user?.is_superuser) && (
        <div className="bg-slate-900 text-slate-100 text-sm text-center py-2 px-4">
          <button
            onClick={() => navigate('/players')}
            className="font-medium hover:underline"
          >
            Enter Player's Secret Corner →
          </button>
        </div>
      )}
      <RecommendationSelector
        onSelectQuick={handleSelectQuick}
        onSelectAdvanced={handleSelectAdvanced}
      />
    </>
  );
};

// Advanced Recommendation Page (Protected - Advanced Users)
const AdvancedRecommendationPage: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [advancedResults, setAdvancedResults] = useState<any | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Review page state
  const [showReview, setShowReview] = useState(false);
  const [formData, setFormData] = useState<any | null>(null);
  const [editingSectionIndex, setEditingSectionIndex] = useState<number | null>(null);

  // ML Recommendations state
  const [mlRecommendations, setMlRecommendations] = useState<MLRecommendationsResponse | null>(null);
  const [ironRecommendations, setIronRecommendations] = useState<any | null>(null);
  const [wedgeRecommendations, setWedgeRecommendations] = useState<any | null>(null);
  const [mlLoading, setMlLoading] = useState(false);
  const [mlError, setMlError] = useState<string | null>(null);
  const [activeEquipmentTab, setActiveEquipmentTab] = useState<'driver' | 'irons' | 'wedges'>('driver');

  // Scoring mode toggle state
  const [scoringMode, setScoringMode] = useState<ScoringMode>('rule_based');

  // Track whether user has existing fittings (lifted from form component)
  const [hasExistingFittings, setHasExistingFittings] = useState(false);

  useEffect(() => {
    const checkFittings = async () => {
      try {
        const data = await getMyProfileCount();
        if (data.total_fittings > 0) {
          setHasExistingFittings(true);
        }
      } catch {
        // Silent fail — prefill banner just won't show
      }
    };
    checkFittings();
  }, []);

  // Handle form completion (go to review page, don't submit yet)
  const handleFormComplete = useCallback((profile: any) => {
    console.log('📋 Advanced: Form complete, showing review page');
    setFormData(profile);
    setShowReview(true);
    setEditingSectionIndex(null);
  }, []);

  // NEW: Handle edit from review page
  const handleEditSection = useCallback((sectionIndex: number) => {
    console.log(`✏️ Advanced: Editing section ${sectionIndex}`);
    setEditingSectionIndex(sectionIndex);
    setShowReview(false);
  }, []);

  // NEW: Handle cancel review (go back to form)
  const handleCancelReview = useCallback(() => {
    console.log('❌ Advanced: Cancelled review, returning to form');
    setEditingSectionIndex(7); // Navigate to last tab "Goals & Challenges"
    setShowReview(false);
  }, []);

  // Handle final confirmation from review page - "Get Your Fit" button
  const handleConfirmSubmit = useCallback(async () => {
    console.log('✅ Advanced: Confirmed, submitting profile and fetching ML recommendations');

    if (!formData) return;

    setLoading(true);
    setError(null);
    setMlError(null);

    try {
      const profileDataWithDemographics = {
        ...formData,
        date_of_birth: user?.date_of_birth,
        gender: user?.gender,
        country: user?.country,
        state: user?.state
      };

      const response = await createAdvancedProfile(profileDataWithDemographics);
      console.log('✅ Advanced: Advanced profile created:', response);

      const profileId = response.profile_id;

      // Step 2: Fetch recommendations based on scoring mode
      setMlLoading(true);
      console.log(`🎯 Advanced: Fetching ${scoringMode} recommendations for profile ${profileId}`);

      let mlResults;
      if (scoringMode === 'ml') {
        mlResults = await getMLDriverRecommendations3Stage(profileId);
        console.log('✅ Advanced: ML recommendations received:', mlResults);
      } else {
        mlResults = await getDriverRecommendationsV9(profileId);
        console.log('✅ Advanced: V9 recommendations received:', mlResults);
      }

      setMlRecommendations(mlResults);
      setActiveEquipmentTab('driver');

      // Fetch iron and wedge recommendations in parallel
      try {
        const [ironResults, wedgeResults] = await Promise.all([
          getIronRecommendationsV9(profileId),
          getWedgeRecommendationsRuleBased(profileId),
        ]);
        setIronRecommendations(ironResults);
        setWedgeRecommendations(wedgeResults);
        console.log('✅ Iron and wedge rule-based results received');
      } catch (ironWedgeErr) {
        console.warn('⚠️ Iron/wedge recommendations failed (non-fatal):', ironWedgeErr);
      }

      setShowReview(false);
      setFormData(null);
      setHasExistingFittings(true);

      // Clear saved form data from localStorage after successful submission
      try {
        localStorage.removeItem('golf-advanced-form-data');
        localStorage.removeItem('golf-advanced-form-section');
      } catch (error) {
        console.error('Error clearing localStorage:', error);
      }

      // Don't show the old advancedResults, show ML recommendations instead

    } catch (err: any) {
      console.error('❌ Advanced: Error:', err);
      const friendlyError = parseValidationError(err);
      setError(friendlyError);
      setMlError(friendlyError);
    } finally {
      setLoading(false);
      setMlLoading(false);
    }
  }, [formData, scoringMode, user]);

  const handleBackToSelector = useCallback(() => {
    navigate('/');
  }, [navigate]);

  const handleBackToForm = useCallback(() => {
    setError(null);
    setAdvancedResults(null);
    setMlRecommendations(null);
    setIronRecommendations(null);
    setWedgeRecommendations(null);
    setActiveEquipmentTab('driver');
    setEditingSectionIndex(null); // Reset to section 0 for a fresh form
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      <ProfessionalHeader user={user} onLogout={logout} />
      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-lg shadow-sm p-6">
          <button
            onClick={mlRecommendations ? handleBackToForm : handleBackToSelector}
            className="mb-6 text-blue-600 hover:text-blue-700 font-medium flex items-center"
          >
            {mlRecommendations ? '← Back to Form' : '← Back to Selection'}
          </button>

          <div className="mb-6">
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
              <div>
                <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Professional Club Fitting</h2>
                <p className="text-gray-600 mt-1 text-sm">Comprehensive analysis with detailed profiling</p>
              </div>
              {!mlRecommendations && (
                <button
                  onClick={() => navigate('/quick-fit')}
                  className="self-start text-sm text-green-ink border border-green-ink px-3 py-1.5 rounded-lg hover:bg-green/10 transition-colors"
                >
                  Multi-player Quick Fit →
                </button>
              )}
            </div>
          </div>

          {mlRecommendations ? (
            /* Recommendations Results with Driver | Irons | Wedges tabs */
            <div>
              {/* Post-Fitting Review banner */}
              <div className="flex justify-end mb-4">
                <button
                  onClick={() => navigate(`/review/${mlRecommendations.metadata.profile_id}`)}
                  className="px-4 py-2 bg-green-ink text-white text-sm font-medium rounded-lg hover:opacity-90 transition-colors"
                >
                  Post-Fitting Review →
                </button>
              </div>

              {/* Tab navigation */}
              <div className="flex border-b border-gray-200 mb-6">
                {[
                  { key: 'driver', label: 'Driver' },
                  { key: 'irons', label: 'Irons' },
                  { key: 'wedges', label: 'Wedges' },
                ].map(tab => (
                  <button
                    key={tab.key}
                    onClick={() => setActiveEquipmentTab(tab.key as 'driver' | 'irons' | 'wedges')}
                    className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                      activeEquipmentTab === tab.key
                        ? 'border-green-ink text-green-ink'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>

              {/* Driver tab */}
              {activeEquipmentTab === 'driver' && (
                <MLRecommendationResults
                  data={mlRecommendations}
                  loading={mlLoading}
                  error={mlError}
                  onBack={handleBackToForm}
                />
              )}

              {/* Irons tab */}
              {activeEquipmentTab === 'irons' && (
                ironRecommendations ? (
                  <MLRecommendationResults
                    data={ironRecommendations}
                    loading={false}
                    error={null}
                    onBack={handleBackToForm}
                  />
                ) : (
                  <div className="text-center py-12 text-gray-500">Iron recommendations not available.</div>
                )
              )}

              {/* Wedges tab */}
              {activeEquipmentTab === 'wedges' && (
                <WedgeRecommendationResults
                  data={wedgeRecommendations}
                  loading={false}
                  error={null}
                  onBack={handleBackToForm}
                />
              )}
            </div>
          ) : showReview && formData ? (
            /* Review Page */
            <>
              {/* Error Display for Review Page */}
              {error && (
                <div className="mb-6 bg-red-50 border-l-4 border-red-400 p-4">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <p className="text-sm text-red-700">{error}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Loading Spinner for Review Page */}
              {loading && (
                <div className="mb-6 bg-blue-50 border-l-4 border-blue-400 p-4">
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600 mr-3"></div>
                    <p className="text-sm text-blue-700">Creating your profile and generating recommendations...</p>
                  </div>
                </div>
              )}

              {/* Scoring Mode Toggle */}
              <ScoringModeToggle
                mode={scoringMode}
                onChange={setScoringMode}
                disabled={loading}
                loading={loading}
                className="mb-6"
              />

              <ProfileReviewPage
                formData={formData}
                onEdit={handleEditSection}
                onConfirm={handleConfirmSubmit}
                onCancel={handleCancelReview}
              />
            </>
          ) : (
            /* Form */
            <AdvancedRecommendationForm
              onSubmit={handleFormComplete}
              loading={loading}
              className=""
              initialSection={editingSectionIndex}
              inReviewMode={formData !== null}
              onReturnToReview={() => setShowReview(true)}
              hasPreviousFittings={hasExistingFittings}
            />
          )}
        </div>
      </main>

    </div>
  );
};

// Main App Content with Routes
// Session expired overlay — shown when token expires mid-session
const SessionExpiredBanner: React.FC = () => {
  const { sessionExpired, clearSessionExpired } = useAuth();
  const navigate = useNavigate();

  if (!sessionExpired) return null;

  const handleSignIn = () => {
    clearSessionExpired();
    navigate('/login');
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full mx-4 p-8 text-center">
        <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <svg className="w-8 h-8 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
          </svg>
        </div>
        <h2 className="text-xl font-semibold text-gray-900 mb-2">Session Expired</h2>
        <p className="text-gray-500 text-sm mb-6">
          Your session has timed out. Please sign in again to continue — your progress has been saved where possible.
        </p>
        <button
          onClick={handleSignIn}
          className="w-full py-3 px-4 bg-green-ink hover:opacity-90 text-white font-medium rounded-lg transition-colors"
        >
          Sign In Again
        </button>
      </div>
    </div>
  );
};

const AppContent: React.FC = () => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { isAuthenticated, isLoading } = useAuth();

  // Show loading spinner while checking auth
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-green-ink mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <Toaster position="top-right" />
      <SessionExpiredBanner />
      <Routes>
        {/* Public Routes */}
        <Route path="/login" element={<LoginSignupPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />

        {/* Public Home Route */}
        <Route path="/" element={<HomePage />} />
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <UserDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/quick"
          element={
            <ProtectedRoute>
              <QuickRecommendationPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/quick-fit"
          element={
            <ProtectedRoute>
              <QuickFitPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/advanced"
          element={
            <ProtectedRoute requireAdvanced>
              <AdvancedRecommendationPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/review/:profileId"
          element={
            <ProtectedRoute>
              <PostFittingReviewPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/fitting/:profileId"
          element={
            <ProtectedRoute>
              <FittingHistoryPage />
            </ProtectedRoute>
          }
        />

        {/* Protected Expert Routes */}
        <Route
          path="/expert"
          element={
            <ProtectedRoute requireExpert>
              <ExpertDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/expert/label"
          element={
            <ProtectedRoute requireExpert>
              <ExpertLabelingPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/expert/bench"
          element={
            <ProtectedRoute requireExpert>
              <FittersCornerPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/expert/bay"
          element={
            <ProtectedRoute requireExpert>
              <BayModePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/expert/explore"
          element={
            <ProtectedRoute requireExpert>
              <ExplorePage />
            </ProtectedRoute>
          }
        />

        {/* Protected Admin Routes */}
        <Route
          path="/admin/users"
          element={
            <ProtectedRoute requireSuperuser>
              <AdminUsersPage />
            </ProtectedRoute>
          }
        />

        {/* My Bag — superuser-only at launch (MY_BAG_PLAN.md §7.3) */}
        <Route
          path="/bag"
          element={
            <ProtectedRoute requireSuperuser>
              <MyBagPage />
            </ProtectedRoute>
          }
        />

        {/* Player's Secret Corner — hidden, pass-holder only */}
        <Route
          path="/players"
          element={
            <ProtectedRoute requireSecretCorner>
              <SecretCorner />
            </ProtectedRoute>
          }
        />

        {/* Redirect unknown routes to home */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  );
};

// Main App Component with AuthProvider
function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

export default App;
